<template>
    <div class="page-container mt-2 pt-1">
        <div class="row mb-3">
            <div class="col-md-4">
                <InputField label="Select Database" type="select" v-model="database" :editable="true"
                    :options="databaseOptions" @update:model-value="handleDatabaseChange" />
            </div>
            <div class="col-md-4" v-if="database">
                <div v-if="tablesLoading" class="text-center">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
                <div v-else>
                    <InputField label="Select Table" type="select" v-model="table" :editable="true"
                        :options="tableOptions" :disabled="!database" />
                </div>
            </div>
        </div>

        <div class="card mb-5" v-if="table">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col">
                        <form @submit.prevent="handleQuestionSubmit">
                            <div class="form-group input-group">
                                <label class="form-label" for="questionInput">Ask your data:</label>
                                <div class="input-group">
                                    <input id="questionInput" ref="questionInput" v-model="question" :disabled="loading"
                                        class="form-control"
                                        placeholder="What was the best campaign from last month?" />
                                    <div class="input-group-append">
                                        <button type="submit" class="btn btn-primary" :disabled="loading || !question">
                                            Let's Go!
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div v-if="queryResponse" class="mt-3">
                    <QueryDisplay title="Response" :query="queryResponse.athena_query"
                        :text-response="queryResponse.text_response" :loading="loading" :question="question"
                        :database="database" :table="table" :resourceId="resourceId" :messagesList="messagesList"
                        @loading-change="loading = $event" @save-query="saveQuery" @append-message="appendToMessagesList" />
                </div>

                <div v-if="loading" class="text-center my-3">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import InputField from "../../components/InputField.vue"
import axios from 'axios'
import { mapActions, mapState } from 'vuex'
import { nextTick } from 'vue'
import QueryDisplay from "../../components/GeneratedQueryDisplay.vue"
import untruncateJson from "untruncate-json";
import { v4 as uuidv4 } from 'uuid';
export default {
    name: "Talk 2 My Data",
    components: { InputField, QueryDisplay },
    data() {
        return {
            database: "",
            table: "",
            question: "",
            loading: false,
            tablesLoading: false,
            databaseOptions: [],
            tableOptions: [],
            queryResponse: null,

            resourceId: null,
            messagesList: [], // for storing the current chat messages and sending for agent's context
        }
    },
    computed: {
        ...mapState({
            currentClient: state => state.currentClient,
            accessToken: state => state.accessToken,
        }),
    },
    methods: {
        ...mapActions({
            showModal: "showModal",
            createChat: "amdp/createChat",
            upsertChatMessage: "amdp/upsertChatMessage",
            updateChatTitle: "amdp/updateChatTitle",
        }),
        untruncateJson,
        appendToMessagesList(message) {
            this.messagesList.push(message);
        },
        async handleQuestionSubmit(e) {
            e.preventDefault();
            this.loading = true;
            this.queryExecuted = false;
            this.queryResponse = null;
            this.queryResults = null;
            this.finalAnswer = null;
            this.resourceId = `chat_${uuidv4()}`; // new resourceId for the chat
            this.messagesList = [];

            // create new chat
            await this.createChat({ 
                feature: "talk_to_my_data",
                create_chat_payload: {
                    resourceId: this.resourceId,
                    cid: this.currentClient.cid,
                }
            }).catch((error) => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error('Failed to create chat:', err);
                this.$store.dispatch('addToast', {
                    type: 'error',
                    message: `Error creating new chat: ${err}`,
                });
            });

            // create user message to chat
            let user_message = {
                "role": "user",
                "content": {
                    "question": this.question,
                    "db_name": this.database,
                    "table_name": this.table,
                },
                "timestamp": new Date().toISOString(),
            }
            await this.upsertChatMessage({
                feature: "talk_to_my_data",
                resourceId: this.resourceId,
                message_payload: {
                    "messages": [user_message]
                },
            }).then(() => {
                this.appendToMessagesList(user_message);
            }).catch((error) => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error('Failed to create user message:', err);
                this.$store.dispatch('addToast', {
                    type: 'error',
                    message: `Error creating user message: ${err}`,
                });
            });

            try {
                const endpoint = `${this.$store.state.agentsUrl}/v1/talk_to_my_data/generate_query`
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${this.accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        question: this.question,
                        db_name: this.database,
                        table_name: this.table,
                        access_token: this.accessToken,
                        cid: this.$store.state.currentClient.cid,
                    })
                });

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let loop = true
                while (loop) {
                    const { value, done } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');

                    // Process all complete lines
                    for (let i = 0; i < lines.length - 1; i++) {
                        const line = lines[i].trim();
                        console.log("line: ", line)
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(this.untruncateJson(line.slice(6)));
                                if (typeof data === 'object' && data.text_response)
                                    this.queryResponse = data;
                            } catch (e) {
                                console.error('Error parsing JSON:', e);
                            }
                        } else if (line.startsWith('reason: ')) {
                            let reason = line.slice(8)
                            let toastoptions = {
                                message: `Blocked by guardrail: ${reason}`,
                                type: "error"
                            }
                            this.$store.dispatch('addToast', toastoptions);

                            // add guardrail message to chat
                            let guardrail_message = {
                                "role": "assistant",
                                "content": {
                                    "guardrail_action": "guardrail_intervened",
                                    "guardrail_message": reason,
                                },
                                "timestamp": new Date().toISOString(),
                            }
                            await this.upsertChatMessage({
                                feature: "talk_to_my_data",
                                resourceId: this.resourceId,
                                message_payload: {
                                    "messages": [guardrail_message]
                                },
                            });
                            return;
                        }
                    }

                    // Keep the last incomplete line in the buffer
                    buffer = lines[lines.length - 1];
                }

                // update chat title
                await this.updateChatTitle({
                    feature: "talk_to_my_data",
                    resourceId: this.resourceId,
                    message_payload: {
                        "messages": [user_message],
                    },
                }).catch((error) => {
                    let err = error.response?.data.error ? error.response.data.error : error.message;
                    console.error('Failed to update chat title:', err);
                });

                // create agent message to chat
                let agent_message = {
                    "role": "assistant",
                    "content": {
                        "text_response": this.queryResponse?.text_response,
                        "athena_query": this.queryResponse?.athena_query,
                    },
                    "timestamp": new Date().toISOString(),
                }
                await this.upsertChatMessage({
                    feature: "talk_to_my_data",
                    resourceId: this.resourceId,
                    message_payload: {
                        "messages": [agent_message]
                    },
                }).then((response) => {
                    console.log("Created agent message:", response.data);
                    this.appendToMessagesList(agent_message);
                }).catch((error) => {
                    console.log("Created agent message(error):", error);
                    let err = error.response?.data.error ? error.response.data.error : error.message;
                    console.error('Failed to create agent message:', err);
                    this.$store.dispatch('addToast', {
                        type: 'error',
                        message: `Error creating agent message: ${err}`,
                    });
                });

            } catch (error) {
                console.error('Error:', error);
            } finally {
                this.loading = false;
            }
        },

        async saveQuery(query) {
            this.showModal({
                component: "AddQuery",
                data: {
                    query: {
                        Name: "",
                        Description: "",
                        QueryString: query,
                        Internal: false,
                        Params: []
                    }
                },
            });
            console.log('Saving query:', query);
        },

        async getDatabases() {
            try {
                const response = await axios.get(
                    `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/dbs`,
                    {
                        headers: {
                            Authorization: `Bearer ${this.accessToken}`,
                        },
                    }
                );

                if (response.data && Array.isArray(response.data)) {
                    this.databaseOptions = response.data.map(db => ({
                        label: db,
                        value: db
                    }));
                }
            } catch (error) {
                console.error('Error fetching databases:', error);
            }
        },

        async handleDatabaseChange(newValue) {
            this.table = "";
            this.tableOptions = [];
            this.tablesLoading = true;

            if (!newValue) return;

            try {
                const response = await axios.get(
                    `${this.$store.state.pailUrl}/${this.$store.state.pailVersion}/cids/${this.$store.state.currentClient.cid}/dbs/${newValue}/tables`,
                    {
                        headers: {
                            Authorization: `Bearer ${this.accessToken}`,
                        },
                    }
                );

                if (response.data) {
                    let tables = response.data.tables
                    if (response.data.views) {
                        tables = tables.concat(response.data.views)
                    }
                    this.tableOptions = tables.map(table => ({
                        label: table,
                        value: table
                    }));
                }
            } catch (error) {
                console.error('Error fetching tables:', error);
            } finally {
                this.tablesLoading = false;
            }
        },

        async scrollToBottom() {
            await nextTick();
            window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: 'smooth'
            });
        },
    },
    mounted() {
        this.getDatabases();
        this.$emit("setChat", null);
    },
    emits: ["setChat"]
}
</script>

<style scoped>
pre {
    white-space: pre-wrap;
    word-wrap: break-word;
}

.table-responsive {
    max-height: 400px;
    overflow-y: auto;
}

table {
    margin-bottom: 0;
}

thead th {
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 1;
}

.page-container {
    padding-bottom: 60px;
    /* Adds space at the bottom of the page */
}

.user-message {
    background-color: rgb(154, 201, 255);
}

.ai-message {
    background-color: rgb(219, 230, 240);
}

.align-right,
.align-left {
    display: flex;
    align-items: flex-start;
    /* Align all items to the top */
    position: relative;
}

.align-right {
    justify-content: flex-end;
    text-align: right;
}

.align-left {
    justify-content: flex-start;
    text-align: left;
}

.message-icon {
    align-self: flex-start !important;
    /* Position icons at the top */
    margin-top: 12px;
    /* Add some spacing from the very top */
}

.message-bubble {
    display: inline-block;
    max-width: 90%;
    word-wrap: break-word;
    padding: 10px;
    border-radius: 6px;
}

.header-content {
    background-color: white;
    padding: 15px;
    border-radius: 7px;
}
</style>